import React, { useRef, useState } from 'react';
import { MapPin, Navigation } from 'lucide-react';
import StoreLocationMap from '../partner/components/StoreLocationMap';
import { captureStoreDevicePosition } from './deviceGeolocation';
import { saveKioskStoreLocation } from './kioskStoreLocationApi';
import '../partner/components/StoreLocationCaptureModal.css';

/**
 * Shown once on kiosk login when the outlet has no saved GPS (geofence on or off).
 */
export default function KioskStoreLocationCaptureModal({
  open,
  franchiseName = '',
  onSaved,
  onCancel,
}) {
  const [step, setStep] = useState('ask');
  const [busy, setBusy] = useState(false);
  const [locking, setLocking] = useState(false);
  const [error, setError] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [pinAdjusted, setPinAdjusted] = useState(false);
  const pinAdjustedRef = useRef(false);

  if (!open) return null;

  const resetCapture = () => {
    setStep('ask');
    setError('');
    setLatitude(null);
    setLongitude(null);
    setAccuracy(null);
    setPinAdjusted(false);
    pinAdjustedRef.current = false;
  };

  const applySample = (sample) => {
    if (pinAdjustedRef.current) return;
    setLatitude(sample.latitude);
    setLongitude(sample.longitude);
    setAccuracy(sample.accuracy);
  };

  const startCapture = async () => {
    setError('');
    setBusy(true);
    setLocking(true);
    setPinAdjusted(false);
    pinAdjustedRef.current = false;
    setStep('capture');
    let gotFix = false;
    try {
      const pos = await captureStoreDevicePosition((sample) => {
        gotFix = true;
        applySample(sample);
      });
      gotFix = true;
      applySample(pos);
    } catch (err) {
      setError(err?.message || 'Could not get location');
      if (!gotFix) setStep('ask');
    } finally {
      setBusy(false);
      setLocking(false);
    }
  };

  const confirmSave = async () => {
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
      setError('Capture your location first.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await saveKioskStoreLocation({ latitude, longitude, accuracy });
      setStep('done');
      window.setTimeout(() => {
        resetCapture();
        onSaved?.();
      }, 900);
    } catch (err) {
      setError(err?.message || 'Failed to save location');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="store-loc-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kiosk-store-loc-title"
    >
      <div className="store-loc-modal-panel">
        <div className="store-loc-modal-header">
          <div className="store-loc-modal-title-row">
            <span className="store-loc-modal-icon" aria-hidden="true">
              <MapPin size={18} />
            </span>
            <h2 id="kiosk-store-loc-title">Set store location</h2>
          </div>
        </div>

        <div className="store-loc-modal-body">
          {step === 'ask' ? (
            <>
              <p className="store-loc-modal-lead">
                {franchiseName ? (
                  <>
                    <strong>{franchiseName}</strong> needs a GPS pin for HQ analytics maps.
                  </>
                ) : (
                  <>This outlet needs a GPS pin for HQ analytics maps.</>
                )}{' '}
                Stand at the store and capture once — you will not be asked again after it is saved.
                Geofence login limits stay separate and only apply when enabled in Workspace.
              </p>
              <div className="store-loc-modal-callout">Are you at the store right now?</div>
              {error ? <p className="store-loc-modal-error">{error}</p> : null}
              <div className="store-loc-modal-actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={startCapture}
                  className="store-loc-modal-btn store-loc-modal-btn--primary"
                >
                  <Navigation size={16} />
                  {busy ? 'Getting location…' : 'Yes — capture now'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetCapture();
                    onCancel?.();
                  }}
                  className="store-loc-modal-btn store-loc-modal-btn--ghost"
                >
                  Cancel sign-in
                </button>
              </div>
            </>
          ) : null}

          {step === 'capture' ? (
            <>
              <p className="store-loc-modal-lead">
                {locking
                  ? 'Reading device GPS…'
                  : 'Confirm the pin on the map, then save.'}
              </p>
              <div className="store-loc-modal-map">
                {typeof latitude === 'number' && typeof longitude === 'number' ? (
                  <StoreLocationMap
                    latitude={latitude}
                    longitude={longitude}
                    interactive={!locking}
                    heightClass="h-[220px]"
                    onChange={({ latitude: lat, longitude: lng }) => {
                      pinAdjustedRef.current = true;
                      setPinAdjusted(true);
                      setLatitude(lat);
                      setLongitude(lng);
                      setAccuracy(null);
                    }}
                  />
                ) : (
                  <p className="store-loc-modal-coords">Waiting for GPS…</p>
                )}
              </div>
              {error ? <p className="store-loc-modal-error">{error}</p> : null}
              <div className="store-loc-modal-actions store-loc-modal-actions--split">
                <button
                  type="button"
                  disabled={busy}
                  onClick={startCapture}
                  className="store-loc-modal-btn store-loc-modal-btn--ghost"
                >
                  Recapture
                </button>
                <button
                  type="button"
                  disabled={
                    busy ||
                    locking ||
                    typeof latitude !== 'number' ||
                    typeof longitude !== 'number'
                  }
                  onClick={confirmSave}
                  className="store-loc-modal-btn store-loc-modal-btn--primary"
                >
                  {busy ? 'Saving…' : 'Save store location'}
                </button>
              </div>
            </>
          ) : null}

          {step === 'done' ? (
            <p className="store-loc-modal-success">Store location saved. Continuing…</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
